import { useState, useRef, useCallback } from 'react';
import { AUDIO_CONSTRAINTS, RECORDING_TIMEOUT, MIME_TYPES } from './constants';
import { getSupportedMimeType } from './utils';

export const useAudioRecorder = (onAudioStop, errorMessages) => {
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimeoutRef = useRef(null);

  const stopAudioRecording = useCallback(() => {
    if (recordingTimeoutRef.current) {
      clearTimeout(recordingTimeoutRef.current);
      recordingTimeoutRef.current = null;
      console.log('Recording timeout cleared');
    }

    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== 'inactive'
    ) {
      mediaRecorderRef.current.stop();
      console.log('Stopping audio recording');
    }
  }, []);

  const startAudioRecording = useCallback(async () => {
    try {
      console.log('Requesting microphone access...');
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: AUDIO_CONSTRAINTS,
      });
      console.log('Microphone access granted');

      audioChunksRef.current = [];

      const { mimeType, options } = getSupportedMimeType(MIME_TYPES);
      console.log('Using MIME type:', mimeType);

      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;

      const actualMimeType =
        mimeType === 'default'
          ? mediaRecorder.mimeType || 'audio/webm'
          : mimeType;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        console.log('MediaRecorder stopped, processing audio...');
        const audioBlob = new Blob(audioChunksRef.current, {
          type: actualMimeType,
        });
        console.log('Audio blob created:', {
          size: audioBlob.size,
          type: actualMimeType,
        });

        stream.getTracks().forEach((track) => track.stop());
        setIsRecording(false);
        await onAudioStop(audioBlob);
      };

      mediaRecorder.start();
      setIsRecording(true);
      console.log('Audio recording started');

      recordingTimeoutRef.current = setTimeout(() => {
        console.log(`Recording timeout reached (${RECORDING_TIMEOUT / 1000}s)`);
        stopAudioRecording();
      }, RECORDING_TIMEOUT);
    } catch (error) {
      console.error('Audio recording error:', {
        name: error.name,
        message: error.message,
      });

      const errorMessage = errorMessages[error.name] || errorMessages.default;
      alert(errorMessage);
      setIsRecording(false);
    }
  }, [onAudioStop, stopAudioRecording, errorMessages]);

  const toggleVoiceInput = useCallback(async () => {
    if (isRecording) {
      stopAudioRecording();
    } else {
      await startAudioRecording();
    }
  }, [isRecording, startAudioRecording, stopAudioRecording]);

  return {
    isRecording,
    toggleVoiceInput,
  };
};
